import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LANGUAGE_OPTIONS } from "../i18n/translations";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Mail, Lock, User, CreditCard, Loader2, Eye, EyeOff, Globe } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_b31fc984-f212-4811-9faa-4930fd1aea96/artifacts/naoml62k_image.png";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    identityNo: "",
    password: "",
    confirmPassword: "",
    nativeLanguage: localStorage.getItem("app_language") || "tr",
    learningLanguage: "en"
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.password) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      localStorage.setItem("app_language", formData.nativeLanguage);
      await register(formData.email, formData.password, formData.fullName, formData.identityNo, formData.nativeLanguage, formData.learningLanguage);
      toast.success("Registration successful!");
      window.location.href = "/dashboard";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          {/* Top spacer to match original vertical position */}
          <div className="h-[30px]" aria-hidden="true" />
          {/* Talkivist logo (KARE versiyon — balonlu) */}
          <img
            src={LOGO_URL}
            alt="Talkivist — Don't Think, Just Speak"
            className="mx-auto w-[210px] max-w-full"
          />
          <p className="text-red-500 mt-9">The Smartest Way to Speak!</p>
        </div>

        <Card className="border-neutral-800 bg-neutral-900/50 backdrop-blur-sm shadow-2xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold text-center text-white font-['Nunito']">Sign Up</CardTitle>
            <CardDescription className="text-center text-neutral-400">
              Create your free account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-neutral-300">Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    placeholder="Your full name"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="pl-10 h-12 bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-red-600"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="example@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 h-12 bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-red-600"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300">ID Number (Optional)</Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input
                    id="identityNo"
                    name="identityNo"
                    type="text"
                    placeholder="National ID Number"
                    value={formData.identityNo}
                    onChange={handleChange}
                    className="pl-10 h-12 bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-red-600"
                    data-testid="register-identity-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300">Native Language *</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500 z-10" />
                  <Select value={formData.nativeLanguage} onValueChange={(v) => setFormData({ ...formData, nativeLanguage: v })}>
                    <SelectTrigger className="pl-10 h-12 bg-neutral-800 border-neutral-700 text-white" data-testid="register-language-select">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      {LANGUAGE_OPTIONS.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code} className="text-white hover:bg-neutral-700">
                          {lang.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300">Language to learn *</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500 z-10" />
                  <Select value={formData.learningLanguage} onValueChange={(v) => setFormData({ ...formData, learningLanguage: v })}>
                    <SelectTrigger className="pl-10 h-12 bg-neutral-800 border-neutral-700 text-white" data-testid="register-learning-language-select">
                      <SelectValue placeholder="Select language to learn" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      <SelectItem value="en" className="text-white hover:bg-neutral-700">🇬🇧 English</SelectItem>
                      <SelectItem value="es" className="text-white hover:bg-neutral-700">🇪🇸 Spanish</SelectItem>
                      <SelectItem value="me" className="text-white hover:bg-neutral-700">🇲🇪 Montenegrin</SelectItem>
                      <SelectItem value="de" className="text-white hover:bg-neutral-700">🇩🇪 German</SelectItem>
                      <SelectItem value="it" className="text-white hover:bg-neutral-700">🇮🇹 Italian</SelectItem>
                      <SelectItem value="pt" className="text-white hover:bg-neutral-700">🇵🇹 Portuguese</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300">Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10 h-12 bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-red-600"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300 transition-colors"
                    data-testid="register-password-toggle"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300">Confirm Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10 pr-10 h-12 bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-red-600"
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300 transition-colors"
                    data-testid="register-confirm-password-toggle"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Sign Up"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-neutral-400">
                Already have an account?{" "}
                <Link to="/login" className="text-red-500 hover:text-red-400 font-semibold" data-testid="login-link">
                  Sign In
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
